import axios from "axios";

/**
 * National Green Tribunal (NGT) case-search API.
 *
 * Five search methods are supported, each with its own captcha/search/details
 * endpoints. `searchType` is one of:
 *   "party-name" | "case-number" | "free-text" | "keyword" | "advocate".
 *
 * NOTE: the original three search types ("party-name", "case-number",
 * "free-text") return flat payloads ({ success, zones, types, cases, ... }) from
 * a `/captcha` endpoint, while the newer "keyword" and "advocate" types expose a
 * `/form` endpoint and wrap their payload under a `data` key
 * ({ success, data: { ... } }). The helpers below normalise both so callers
 * always receive the same flat shape.
 *
 * All search/details calls require the CAPTCHA solution and the session
 * `cookies` array returned by the matching captcha/form endpoint.
 */
const NGT_API_BASE_URL = "https://ngt-backend-596718606544.asia-south1.run.app/api";

// Search types whose backend wraps responses under a `data` key and serve the
// form options from `/form` instead of `/captcha`.
const WRAPPED_TYPES = new Set(["keyword", "advocate"]);

/**
 * Fetch the CAPTCHA image, form options (zones + types / keywords) and session
 * cookies for a given search type.
 *
 * GET /{searchType}/captcha   (party-name | case-number | free-text)
 * GET /{searchType}/form      (keyword | advocate)
 *
 * Returns (normalised, flat):
 *   { success, zones, types?, keywords?, subKeywords?, captchaImage, cookies }
 */
export const getNgtCaptcha = async (searchType) => {
  const isWrapped = WRAPPED_TYPES.has(searchType);
  const endpoint = isWrapped ? "form" : "captcha";
  const res = await axios.get(`${NGT_API_BASE_URL}/${searchType}/${endpoint}`, {
    headers: { "Content-Type": "application/json" },
  });

  if (isWrapped) {
    if (res.data?.success === false) {
      return { success: false, error: res.data.error };
    }
    return { success: true, ...(res.data?.data || {}) };
  }
  return res.data;
};

/**
 * Fetch the main keyword list (keyword1) for a zone. Keyword options are loaded
 * dynamically per-zone on the source site, so they are not part of the initial
 * form payload.
 *
 * POST /keyword/keywords  { zone_type }
 * Returns: [{ value, label }]
 */
export const getNgtKeywords = async (zone_type) => {
  const res = await axios.post(
    `${NGT_API_BASE_URL}/keyword/keywords`,
    { zone_type },
    { headers: { "Content-Type": "application/json" } }
  );
  return Array.isArray(res.data?.keywords) ? res.data.keywords : [];
};

/**
 * Fetch the sub-keyword list (keyword2) for a zone + selected parent keyword.
 *
 * POST /keyword/subkeywords  { zone_type, parent_keyword_id }
 * Returns: [{ value, label }]
 */
export const getNgtSubKeywords = async (zone_type, parent_keyword_id) => {
  const res = await axios.post(
    `${NGT_API_BASE_URL}/keyword/subkeywords`,
    { zone_type, parent_keyword_id },
    { headers: { "Content-Type": "application/json" } }
  );
  return Array.isArray(res.data?.subKeywords) ? res.data.subKeywords : [];
};

/**
 * Search NGT cases. The `params` shape depends on `searchType`:
 *  - party-name : { zone_type, party_type, party_name, case_year, status, captcha_input, cookies }
 *  - case-number: { zone_type, case_type, case_number, case_year, captcha_input, cookies }
 *  - free-text  : { zone_type, search_by, text_name, captcha_input, cookies }
 *  - keyword    : { zone_type, keyword1, keyword2, captcha_input, cookies }
 *  - advocate   : { zone_type, advocate_name, case_year, captcha_input, cookies }
 *
 * POST /{searchType}/search
 * Returns (normalised): { success, cases:[{ srNo, zonalBench, diaryNumber,
 *   caseNumber, partyName, registrationDate, status, caseUrl }] }
 */
export const searchNgtCases = async (searchType, params) => {
  const res = await axios.post(`${NGT_API_BASE_URL}/${searchType}/search`, params, {
    headers: { "Content-Type": "application/json" },
  });

  if (WRAPPED_TYPES.has(searchType)) {
    if (res.data?.success === false) {
      return { success: false, error: res.data.error };
    }
    // Inner payload is the parser result: { success, cases } | { success:false, error }
    return res.data?.data || { success: true, cases: [] };
  }
  return res.data;
};

/**
 * Fetch full details of a single NGT case using the `caseUrl` from search
 * results.
 *
 * POST /{searchType}/details
 * Returns (normalised): { caseInfo, parties:{petitioners,respondents},
 *   listingHistory, iaMa, connectedMatters }
 */
export const getNgtCaseDetails = async (searchType, caseUrl, cookies) => {
  const res = await axios.post(
    `${NGT_API_BASE_URL}/${searchType}/details`,
    { caseUrl, cookies },
    { headers: { "Content-Type": "application/json" } }
  );

  if (WRAPPED_TYPES.has(searchType)) {
    if (res.data?.success === false) {
      return { success: false, error: res.data.error };
    }
    return res.data?.data || {};
  }
  return res.data;
};

// ─────────────────────────────────────────────────────────────────────────────
// Judgements & Orders
//
// A distinct NGT feature from Case-Status search (the source site exposes it
// under "Judgement / Order"). Four search methods share a single captcha
// endpoint and all return the same flat shape:
//   { success, judgements: [{ sNo, bench, diaryNumber, caseNumber, partyName,
//     dateOfOrder, status, pdfUrl }] }
//   | { success: false, error }
// ─────────────────────────────────────────────────────────────────────────────
const JUDGEMENT_BASE_URL = `${NGT_API_BASE_URL}/judgement-orders`;

// Maps a judgement search method to its backend POST endpoint.
const JUDGEMENT_ENDPOINTS = {
  date: `${JUDGEMENT_BASE_URL}/search`,
  "case-number": `${JUDGEMENT_BASE_URL}/search/case-number`,
  "party-name": `${JUDGEMENT_BASE_URL}/search/party-name`,
  judge: `${JUDGEMENT_BASE_URL}/search/judge`,
};

/**
 * Fetch the CAPTCHA image, zonal-bench list and session cookies for a
 * judgement/order search. The same captcha session is reused across all four
 * judgement search methods.
 *
 * GET /judgement-orders/captcha
 * Returns: { success, zones:[{value,label}], searchByTypes:[{value,label}],
 *   captchaImage, cookies }
 */
export const getJudgementCaptcha = async () => {
  const res = await axios.get(`${JUDGEMENT_BASE_URL}/captcha`, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
};

/**
 * Search NGT judgements/orders. `searchType` selects the endpoint and the
 * required `params` shape (all also need `captcha_input` + `cookies`):
 *  - date        : { zone_type, from_date, to_date, search_type }
 *  - case-number : { zone_type, case_type, case_no, case_year, search_type }
 *  - party-name  : { zone_type, party_type, party_name, search_type }
 *  - judge       : { zone_type, judge_name, from_date, to_date, search_type }
 *                  (judge_name may be a numeric id OR a name — the backend
 *                   resolves names to ids automatically)
 *
 * Dates are dd/mm/yyyy. Returns: { success, judgements:[...] } | { success:false, error }
 */
export const searchJudgements = async (searchType, params) => {
  const url = JUDGEMENT_ENDPOINTS[searchType];
  if (!url) throw new Error(`Unknown judgement search type: ${searchType}`);
  const res = await axios.post(url, params, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
};

/**
 * Fetch full details for a single judgement/order row. The source site keys the
 * detail page by zonal bench + filing (diary) number, so both are required
 * along with the session `cookies` from the search.
 *
 * POST /judgement-orders/detail  { zone, filingNumber, cookies }
 * Returns: { success, data: { <Field_Label>: value, ..., pdfLinks:[{type,url}] } }
 *   | { success:false, error }
 * The `data` keys are dynamic (underscored field labels scraped from the page),
 * so callers should render them generically rather than assuming fixed keys.
 */
export const getJudgementDetail = async (zone, filingNumber, cookies) => {
  const res = await axios.post(
    `${JUDGEMENT_BASE_URL}/detail`,
    { zone, filingNumber, cookies },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export { NGT_API_BASE_URL };
